/**
 * Materialstammkarte für Einkauf-Inventar
 * Navigation durch Materialien mit Modal-Ansicht
 */

interface MaterialkarteData {
    id: number;
    name: string;
    typName: string;
    einheit: string;
    menge: number;
    minMenge: number;
    status: 'rot' | 'gelb' | 'gruen';
    intervall: number;
    letzteInventur: string;
    notiz: string;
    farbe?: string;
    farbe2?: string;
    fotoUrl?: string;
    lagerortAbteilung: string;
    lagerortCustom: string;
}

class MaterialkarteNavigator {
    private ids: number[] = [];
    private currentIndex: number = 0;
    private rows: Map<number, HTMLElement>;

    constructor() {
        this.ids = [];
        this.currentIndex = 0;
        this.rows = new Map<number, HTMLElement>();
        this.init();
    }

    /**
     * Initialisiert die Navigation mit allen Inventar-Rows
     */
    init(): void {
        const rows = document.querySelectorAll<HTMLElement>('.inventar-row');
        rows.forEach((row, index) => {
            const id = row.getAttribute('data-id');
            if (id) {
                const numId = parseInt(id, 10);
                this.ids.push(numId);
                this.rows.set(numId, row as HTMLTableRowElement);
            }
        });
        this.updateNavigation();
    }

    /**
     * Öffnet Materialkarte für bestimmte ID
     */
    open(invId: number): void {
        const row = this.rows.get(invId);
        if (!row) {
            console.error(`Inventar-Position ${invId} nicht gefunden`);
            return;
        }

        const data = this.extractDataFromRow(row);
        this.renderModal(data);
        
        // Index aktualisieren
        this.currentIndex = this.ids.indexOf(invId);
        if (this.currentIndex === -1) {
            this.currentIndex = 0;
        }
        
        this.updateNavigation();
        this.showModal();
    }

    /**
     * Navigiert zum nächsten/vorherigen Material
     */
    navigate(direction: number): void {
        if (this.ids.length === 0) return;

        this.currentIndex += direction;

        // Index begrenzen
        if (this.currentIndex < 0) this.currentIndex = 0;
        if (this.currentIndex >= this.ids.length) this.currentIndex = this.ids.length - 1;

        const currentId = this.ids[this.currentIndex];
        this.open(currentId);
    }

    /**
     * Extrahiert Daten aus dem Row-Element
     */
    private extractDataFromRow(row: HTMLElement): MaterialkarteData {
        const decodeHTML = (str: string | null): string => {
            if (!str) return '';
            const txt = document.createElement('textarea');
            txt.innerHTML = str;
            return txt.value;
        };

        return {
            id: parseInt(row.getAttribute('data-id') || '0', 10),
            name: decodeHTML(row.getAttribute('data-full-name')) || '—',
            typName: decodeHTML(row.getAttribute('data-typ-name')) || '—',
            einheit: decodeHTML(row.getAttribute('data-einheit')) || 'Stück',
            menge: parseFloat(row.getAttribute('data-menge') || '0'),
            minMenge: parseFloat(row.getAttribute('data-min-menge') || '0'),
            status: (row.getAttribute('data-status') as MaterialkarteData['status']) || 'gruen',
            intervall: parseInt(row.getAttribute('data-intervall') || '0', 10),
            letzteInventur: decodeHTML(row.getAttribute('data-letzte-inventur')) || '—',
            notiz: decodeHTML(row.getAttribute('data-notiz')) || '',
            farbe: row.getAttribute('data-farbe') || undefined,
            farbe2: row.getAttribute('data-farbe2') || undefined,
            fotoUrl: row.getAttribute('data-foto') || undefined,
            lagerortAbteilung: decodeHTML(row.getAttribute('data-lagerort-abteilung')) || '',
            lagerortCustom: decodeHTML(row.getAttribute('data-lagerort-custom')) || ''
        };
    }

    /**
     * Rendert die Modal-Inhalte
     */
    private renderModal(data: MaterialkarteData): void {
        // Titel und Typ
        const titleEl = document.getElementById('mkTitle');
        const typEl = document.getElementById('mkTyp');
        if (titleEl) titleEl.textContent = data.name;
        if (typEl) typEl.textContent = data.typName;

        // Mengen
        const mengeEl = document.getElementById('mkMenge');
        const minMengeEl = document.getElementById('mkMinMenge');
        if (mengeEl) mengeEl.textContent = `${data.menge} ${data.einheit}`;
        if (minMengeEl) minMengeEl.textContent = `${data.minMenge} ${data.einheit}`;

        // Inventur
        const intervallEl = document.getElementById('mkIntervall');
        const letzteInventurEl = document.getElementById('mkLetzteInventur');
        if (intervallEl) intervallEl.textContent = `${data.intervall} Tage`;
        if (letzteInventurEl) letzteInventurEl.textContent = data.letzteInventur;

        // Notiz
        const notizEl = document.getElementById('mkNotiz');
        if (notizEl) notizEl.textContent = data.notiz || 'Keine Notizen';

        // Lagerort
        const lagerortEl = document.getElementById('mkLagerort');
        if (lagerortEl) {
            let lagerortText = data.lagerortAbteilung;
            if (data.lagerortCustom) {
                lagerortText += (lagerortText ? ' • ' : '') + data.lagerortCustom;
            }
            lagerortEl.textContent = lagerortText || '—';
        }

        // Status
        const statusEl = document.getElementById('mkStatus');
        if (statusEl) {
            let statusHtml = '';
            switch (data.status) {
                case 'rot':
                    statusHtml = '<span class="badge bg-danger">🔴 Unter Minimum</span>';
                    break;
                case 'gelb':
                    statusHtml = '<span class="badge bg-warning text-dark">🟡 Inventur fällig</span>';
                    break;
                default:
                    statusHtml = '<span class="badge bg-success">✅ OK</span>';
            }
            statusEl.innerHTML = statusHtml;
        }

        // Farbe
        this.renderFarbe(data);

        // Foto
        this.renderFoto(data);

        // Bearbeiten-Button
        const bearbeitenBtn = document.getElementById('mkBearbeitenBtn') as HTMLAnchorElement;
        if (bearbeitenBtn) {
            bearbeitenBtn.href = `/maschinen/inventar/${data.id}/update_abteilung/`;
        }
    }

    /**
     * Rendert Farbe-Sektion
     */
    private renderFarbe(data: MaterialkarteData): void {
        const farbeSection = document.getElementById('mkFarbeSection');
        const farbeNone = document.getElementById('mkFarbeNone');
        
        if (!farbeSection || !farbeNone) return;

        if (data.farbe) {
            farbeSection.style.display = 'block';
            farbeNone.style.display = 'none';
            
            const swatchEl = document.getElementById('mkFarbeSwatch');
            const hexEl = document.getElementById('mkFarbeHex');
            
            if (swatchEl && hexEl) {
                if (data.farbe2) {
                    // Streifen-Muster
                    swatchEl.style.background = `linear-gradient(90deg, 
                        ${data.farbe} 0%, ${data.farbe} 12.5%, 
                        ${data.farbe2} 12.5%, ${data.farbe2} 25%, 
                        ${data.farbe} 25%, ${data.farbe} 37.5%, 
                        ${data.farbe2} 37.5%, ${data.farbe2} 50%, 
                        ${data.farbe} 50%, ${data.farbe} 62.5%, 
                        ${data.farbe2} 62.5%, ${data.farbe2} 75%, 
                        ${data.farbe} 75%, ${data.farbe} 87.5%, 
                        ${data.farbe2} 87.5%, ${data.farbe2} 100%)`;
                    hexEl.textContent = `${data.farbe.toUpperCase()} / ${data.farbe2.toUpperCase()}`;
                } else {
                    swatchEl.style.background = data.farbe;
                    hexEl.textContent = data.farbe.toUpperCase();
                }
            }
        } else {
            farbeSection.style.display = 'none';
            farbeNone.style.display = 'block';
        }
    }

    /**
     * Rendert Foto-Sektion
     */
    private renderFoto(data: MaterialkarteData): void {
        const fotoCard = document.getElementById('mkFotoCard');
        const fotoImg = document.getElementById('mkFoto') as HTMLImageElement;
        
        if (fotoCard && fotoImg && data.fotoUrl) {
            fotoCard.style.display = 'block';
            fotoImg.src = data.fotoUrl;
            fotoImg.alt = `Foto von ${data.name}`;
        } else if (fotoCard) {
            fotoCard.style.display = 'none';
        }
    }

    /**
     * Aktualisiert Navigation-Buttons und Counter
     */
    private updateNavigation(): void {
        const counterEl = document.getElementById('mkCounter');
        const prevBtn = document.getElementById('mkNavPrev') as HTMLButtonElement;
        const nextBtn = document.getElementById('mkNavNext') as HTMLButtonElement;

        if (counterEl) {
            counterEl.textContent = `${this.currentIndex + 1} / ${this.ids.length}`;
        }

        if (prevBtn) {
            prevBtn.disabled = this.currentIndex === 0;
        }

        if (nextBtn) {
            nextBtn.disabled = this.currentIndex >= this.ids.length - 1;
        }
    }

    /**
     * Zeigt das Modal
     */
    private showModal(): void {
        const modalEl = document.getElementById('materialkarteModal');
        if (modalEl) {
            // @ts-ignore - Bootstrap Modal ist nicht typisiert
            const modal = new bootstrap.Modal(modalEl);
            
            // Editor aktuelle ID setzen
            // @ts-ignore
            if (window.setMaterialkarteInvId) {
                // @ts-ignore
                window.setMaterialkarteInvId(this.ids[this.currentIndex]);
            }
            
            modal.show();
        }
    }

    /**
     * Gibt aktuelle Position zurück
     */
    getPosition(): { current: number; total: number } {
        return {
            current: this.currentIndex + 1,
            total: this.ids.length
        };
    }
}

// Globale Instanz erstellen (nur wenn noch nicht vorhanden)
// @ts-ignore - Globale Funktion für Template
// @ts-ignore
if (!(window as any).materialkarteNavigator) {
    // @ts-ignore
    (window as any).materialkarteNavigator = new MaterialkarteNavigator();
}

// @ts-ignore - Globale Funktion für Template
// @ts-ignore
if (!(window as any).openMaterialkarte) {
    // @ts-ignore
    (window as any).openMaterialkarte = (invId: number) => {
        // @ts-ignore
        (window as any).materialkarteNavigator.open(invId);
    };
}

// @ts-ignore
if (!(window as any).navigateMaterialkarte) {
    // @ts-ignore
    (window as any).navigateMaterialkarte = (direction: number) => {
        // @ts-ignore
        (window as any).materialkarteNavigator.navigate(direction);
    };
}

// Initialisierung beim Laden
document.addEventListener('DOMContentLoaded', () => {
    // @ts-ignore
    if (!window.materialkarteNavigator) {
        // @ts-ignore
        window.materialkarteNavigator = new MaterialkarteNavigator();
    }
});
